import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

/* ── Page 7 ── */

const p7Faqs: LearnFAQ[] = [
  {
    question:
      "What is the most effective non-medication treatment for depression?",
    answer:
      "It depends on severity. For mild-to-moderate major depression, structured psychotherapy such as cognitive behavioral therapy, behavioral activation, or interpersonal therapy has the strongest evidence and performs comparably to medication for many people. For severe or treatment-resistant depression, device-based treatments have the strongest evidence: ECT has the highest remission rates for severe depression, and rTMS and SAINT are FDA-cleared for adults who have not improved on antidepressants. Exercise and light therapy are helpful additions rather than stand-alone treatments for severe depression.",
  },
  {
    question: "Is there a natural antidepressant that actually works?",
    answer:
      "St John's wort has trial evidence for mild-to-moderate depression, but results vary by product and it interacts with many prescription drugs, including antidepressants and hormonal birth control. Omega-3 fatty acids and SAMe have mixed, mostly small-study evidence. No supplement is FDA-approved to treat depression, and none has been shown to work for severe depression. Exercise is arguably the best-supported natural option. Discuss any supplement with your prescriber before starting it.",
  },
  {
    question: "Can I treat depression at home without seeing anyone?",
    answer:
      "Mild symptoms sometimes improve with regular exercise, consistent sleep, daylight, reduced alcohol, and social contact, and self-guided CBT programs can help. However, depression that lasts more than a few weeks, interferes with work or self-care, or involves any thoughts of death should be evaluated by a professional. Home remedies for depression are a reasonable starting point for mild symptoms, not a substitute for treatment when symptoms are moderate or severe.",
  },
  {
    question:
      "How do I fight depression without drugs if antidepressants have already failed?",
    answer:
      "If two or more antidepressants have not produced satisfactory improvement, you may meet common definitions of treatment-resistant depression. Non-drug options at that stage include intensive psychotherapy, repetitive TMS, the accelerated SAINT protocol, and ECT. TMS and SAINT are FDA-cleared specifically for adults whose depression has not improved on antidepressant medication and are delivered awake in an outpatient office without anesthesia. A psychiatric evaluation can clarify which you are eligible for.",
  },
  {
    question: "Is TMS considered a medication?",
    answer:
      "No. Transcranial magnetic stimulation is a medical device treatment, not a drug. It uses magnetic pulses to stimulate a specific brain region and introduces no chemical into the body. TMS devices are FDA-cleared rather than FDA-approved, which is the terminology used for medications. Many people continue an antidepressant during TMS, but TMS itself can be delivered without medication when a clinician judges that appropriate.",
  },
  {
    question: "Can I stop my antidepressant if I start therapy or TMS?",
    answer:
      "Not on your own. Stopping abruptly can cause discontinuation symptoms and raises the risk of relapse. If you want to reduce or stop medication, ask your prescriber to plan a gradual taper and to decide the timing relative to any new treatment. Psychotherapy and TMS are both commonly delivered alongside medication, and combining approaches is often more effective than any single one.",
  },
  {
    question: "Are there over-the-counter medicines for depression?",
    answer:
      "No. In the United States there is no over-the-counter drug approved by the FDA to treat depression. Products sold for mood support are dietary supplements, which are not reviewed for effectiveness before sale and may not legally claim to treat disease. Some, particularly St John's wort, have important drug interactions. If cost or access to a prescriber is the barrier, community mental health centers and telehealth services are safer routes than self-treating with supplements.",
  },
  {
    question: "What natural remedies help depression and anxiety together?",
    answer:
      "Exercise, regular sleep, reduced caffeine and alcohol, and mindfulness- or CBT-based skills have evidence for both depressive and anxiety symptoms. Psychotherapy is effective for both and is usually the first recommendation when they occur together. Some TMS devices are FDA-cleared for anxiety symptoms in adults with major depression, though not for anxiety disorders alone. Supplements marketed for anxiety have limited evidence and can interact with medications.",
  },
];

export const page7: LearnPageData = {
  slug: "depression-treatment-without-medication",
  title: "Depression Treatment Without Medication: What Works",
  description:
    "Evidence-based non-medication treatments for depression: therapy, exercise, light therapy, supplements, and FDA-cleared TMS and SAINT, with risks explained.",
  canonical: "/learn/depression-treatment-without-medication",
  h1: "What Are the Options for Treating Depression Without Medication?",
  breadcrumbLabel: "Depression Treatment Without Medication",
  crumbs: baseCrumbs("Depression Treatment Without Medication"),
  shortAnswer:
    "Non-medication options for depression include psychotherapy (CBT, behavioral activation), exercise, light therapy, and FDA-cleared device treatments like TMS and SAINT®. TMS is a device, not a drug. Call Neuro Wellness TMS Centers of America at 954-827-9793.",
  keyTakeaways: [
    "For mild–moderate depression, psychotherapy (CBT, behavioral activation, IPT) has the strongest non-drug evidence.",
    "For severe/TRD, FDA-cleared device treatments (TMS, SAINT®) and ECT have the strongest evidence.",
    "TMS is a medical device, not a drug — it introduces no chemical into the body.",
    "No supplement is FDA-approved for depression; St John's wort has important drug interactions.",
    "Do not stop antidepressants abruptly — taper with your prescriber, especially when starting a new treatment.",
  ],
  sections: [
    {
      heading: "Psychotherapy",
      body: (
        <>
          {P(
            "For mild-to-moderate major depression, structured psychotherapy has the strongest non-medication evidence. Cognitive behavioral therapy (CBT), behavioral activation, and interpersonal therapy perform comparably to medication for many people. Therapy is frequently part of a TMS treatment plan and is often covered separately by insurance.",
          )}
        </>
      ),
    },
    {
      heading: "Lifestyle: exercise, sleep, light therapy",
      body: (
        <>
          {P(
            "Regular exercise, consistent sleep, daylight exposure, and reduced alcohol have evidence for depressive symptoms. Light therapy has evidence for seasonal affective disorder. These are helpful additions and reasonable starting points for mild symptoms, but they are not stand-alone treatments for severe depression.",
          )}
        </>
      ),
    },
    {
      heading: "Supplements",
      body: (
        <>
          {P(
            "St John's wort has trial evidence for mild-to-moderate depression, but results vary by product and it interacts with many prescription drugs, including antidepressants and hormonal birth control. Omega-3 fatty acids and SAMe have mixed, mostly small-study evidence. No supplement is FDA-approved to treat depression, and none has been shown to work for severe depression. Discuss any supplement with your prescriber before starting it.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status: TMS is a device, not a drug",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> TMS devices (including SAINT® on
            the Magnus system) are FDA-cleared, not FDA-approved — approval is
            the process for drugs. TMS uses magnetic pulses to stimulate a
            specific brain region and introduces no chemical into the body. It
            is a non-invasive medical device treatment, delivered awake in an
            outpatient office without anesthesia. No over-the-counter drug is
            FDA-approved to treat depression.
          </p>
        </div>
      ),
    },
    {
      heading: "Device-based treatments: TMS and SAINT®",
      body: (
        <>
          {P(
            "For severe or treatment-resistant depression, device-based treatments have the strongest evidence. ECT has the highest remission rates for severe or psychotic depression but carries anesthesia and cognitive risks. rTMS and SAINT® are FDA-cleared for adults whose depression has not improved on antidepressant medication and are delivered awake in an outpatient office without anesthesia.",
          )}
        </>
      ),
    },
    {
      heading: "If antidepressants have already failed",
      body: (
        <>
          {P(
            "If two or more antidepressants have not produced satisfactory improvement, non-drug options include intensive psychotherapy, repetitive TMS, the accelerated SAINT® protocol, and ECT. TMS and SAINT® are FDA-cleared specifically for adults whose depression has not improved on antidepressant medication. Do not stop current medication on your own — ask your prescriber to plan a gradual taper.",
          )}
        </>
      ),
    },
  ],
  faqs: p7Faqs,
  sources: [
    {
      citation:
        "Cuijpers P, et al. The effects of psychotherapy for adult depression are overestimated. Psychother Psychosom. 2014;83(5):269-278.",
    },
    {
      citation:
        "Blumenthal JA, et al. Exercise and pharmacotherapy in the treatment of major depressive disorder. Psychosom Med. 2007;69(7):587-596.",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/depression-treatment-without-medication#webpage`,
        name: "What Are the Options for Treating Depression Without Medication?",
        url: `${SITE_URL}/learn/depression-treatment-without-medication`,
        description:
          "Evidence-based non-medication treatments for depression: therapy, exercise, light therapy, supplements, and FDA-cleared TMS and SAINT, with risks explained.",
        about: [
          {
            "@type": "MedicalCondition",
            name: "Major depressive disorder",
            code: {
              "@type": "MedicalCode",
              code: "D003865",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Treatment-resistant depression",
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        name: "Psychotherapy for depression",
        alternateName: [
          "Cognitive behavioral therapy",
          "Behavioral activation",
          "Interpersonal therapy",
        ],
      },
      {
        "@type": "MedicalTherapy",
        name: "Repetitive transcranial magnetic stimulation (rTMS)",
        alternateName: "TMS therapy",
        procedureType: "Noninvasive",
        relevantSpecialty: { "@type": "MedicalSpecialty", name: "Psychiatry" },
      },
      {
        "@type": "MedicalTherapy",
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT)",
        alternateName: "Magnus Neuromodulation System with SAINT Technology",
        procedureType: "Noninvasive",
      },
      faqJsonld(
        p7Faqs,
        `${SITE_URL}/learn/depression-treatment-without-medication#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Depression Treatment Without Medication")),
    ],
  },
  relatedLinks: sharedRelated,
};
